import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from './Table'
import { Button } from './Button'
import type { Column } from './Table'
import type { ButtonVariant } from './Button'

/** Minimal org shape required by this panel — matches Organization from the Redux slice. */
export interface OrgSummary {
  /** Unique identifier used for selection and action callbacks. */
  id: string
  name: string
  description: string
  createdAt: string
}

interface Props {
  title: string
  orgs: OrgSummary[]
  approveLabel?: string
  /** Visual style for the single-row approve button. Defaults to 'success'. */
  approveVariant?: ButtonVariant
  bulkAcceptLabel?: string
  /** Visual style for the bulk-accept button. Defaults to 'primary'. */
  bulkAcceptVariant?: ButtonVariant
  bulkRejectLabel?: string
  onBulkAccept?: (ids: string[]) => void
  onBulkReject?: (ids: string[]) => void
  onApprove: (id: string) => void
  onReject?: (id: string) => void
  onRejectWithMessage?: (id: string) => void
  onEditFeatures?: (id: string) => void
}

/** Panel listing organizations using the shared Table with selection and bulk actions. */
export function OrgPanel({
  title,
  orgs,
  approveLabel,
  approveVariant = 'success',
  bulkAcceptLabel,
  bulkAcceptVariant = 'primary',
  bulkRejectLabel,
  onBulkAccept,
  onBulkReject,
  onApprove,
  onReject,
  onRejectWithMessage,
  onEditFeatures,
}: Props) {
  const { t, i18n } = useTranslation()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const someSelected = selected.size > 0
  const selectedIds = Array.from(selected)

  function handleSelectAll(checked: boolean) {
    setSelected(checked ? new Set(orgs.map((o) => o.id)) : new Set())
  }

  function handleSelectRow(key: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(key)
      } else {
        next.delete(key)
      }
      return next
    })
  }

  const columns: Column<OrgSummary>[] = [
    {
      header: 'Organization',
      render: (org) => (
        <div className="min-w-0">
          <p className="text-pax-black truncate text-sm font-semibold">{org.name}</p>
        </div>
      ),
    },
    {
      header: 'Registered',
      headerClassName: 'w-28 text-right',
      cellClassName: 'w-28 text-right',
      render: (org) => (
        <span className="text-pax-gray text-xs">
          {new Date(org.createdAt).toLocaleDateString(i18n.language)}
        </span>
      ),
    },
    {
      header: 'Actions',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      render: (org) => (
        <div className="flex flex-wrap justify-end gap-2">
          {onEditFeatures && (
            <Button variant="ghost" size="sm" onClick={() => onEditFeatures(org.id)}>
              {t('btn_edit_features')}
            </Button>
          )}
          <Button variant={approveVariant} size="sm" onClick={() => onApprove(org.id)}>
            {approveLabel ?? t('btn_approve')}
          </Button>
          {onReject && (
            <Button variant="danger" size="sm" onClick={() => onReject(org.id)}>
              {t('btn_reject')}
            </Button>
          )}
          {onRejectWithMessage && (
            <Button variant="secondary" size="sm" onClick={() => onRejectWithMessage(org.id)}>
              {t('btn_reject_message')}
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="border-pax-purple/30 flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm">
      {/* Panel header — stacks into two rows on narrow screens (title row, then bulk-actions
          row) instead of cramming both onto one line, which used to push the bulk buttons past
          the card edge and force the whole panel to scroll horizontally. */}
      <div className="border-pax-purple/20 flex flex-col gap-2 border-b px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <h2 className="text-pax-black text-sm font-semibold">{title}</h2>
          <span className="bg-pax-light-purple text-pax-blue rounded-full px-2 py-0.5 text-xs font-semibold">
            {orgs.length}
          </span>
        </div>

        {/* Bulk actions — shown when rows are selected */}
        {someSelected && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span className="text-pax-black text-sm font-medium">
              {t('n_selected', { count: selected.size })}
            </span>
            {bulkAcceptLabel && onBulkAccept && (
              <Button
                variant={bulkAcceptVariant}
                size="sm"
                onClick={() => onBulkAccept(selectedIds)}
              >
                {bulkAcceptLabel}
              </Button>
            )}
            {bulkRejectLabel && onBulkReject && (
              <Button variant="secondary" size="sm" onClick={() => onBulkReject(selectedIds)}>
                {bulkRejectLabel}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Table (desktop) + cards (mobile) */}
      <Table
        columns={columns}
        rows={orgs}
        keyExtractor={(o) => o.id}
        emptyMessage={t('no_organizations')}
        selectable
        selectedKeys={selected}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        rowAriaLabel={(o) => t('table_select_row', { name: o.name })}
        containerClassName="overflow-hidden"
        // This panel already wraps the table in its own white card shell, so on mobile the
        // individual row-cards would otherwise sit white-on-white with only a faint border
        // between them. A tinted, padded backdrop behind the card list makes each row read as
        // a clearly separate card instead of a continuous block.
        cardsContainerClassName="bg-pax-light-purple/40 flex flex-col gap-3 p-3 md:hidden"
        renderCard={(org, isSelected, onSelect) => (
          <div
            className={[
              'border-pax-purple/30 rounded-2xl border bg-white p-4 shadow-md transition-colors',
              isSelected ? 'bg-pax-light-blue/40' : '',
            ].join(' ')}
          >
            <div className="mb-3 flex items-start gap-3">
              {/* Checkbox */}
              <input
                type="checkbox"
                aria-label={t('table_select_row', { name: org.name })}
                checked={isSelected}
                onChange={(e) => onSelect(e.target.checked)}
                className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
              />
              <div className="min-w-0 flex-1">
                <p className="text-pax-black font-semibold">{org.name}</p>
                <p className="text-pax-gray mt-1 text-xs">
                  {new Date(org.createdAt).toLocaleDateString(i18n.language)}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {onEditFeatures && (
                <Button variant="ghost" size="sm" fullWidth onClick={() => onEditFeatures(org.id)}>
                  {t('btn_edit_features')}
                </Button>
              )}
              <Button
                variant={approveVariant}
                size="sm"
                fullWidth
                onClick={() => onApprove(org.id)}
              >
                {approveLabel ?? t('btn_approve')}
              </Button>
              {onReject && (
                <Button variant="danger" size="sm" fullWidth onClick={() => onReject(org.id)}>
                  {t('btn_reject')}
                </Button>
              )}
              {onRejectWithMessage && (
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={() => onRejectWithMessage(org.id)}
                >
                  {t('btn_reject_message')}
                </Button>
              )}
            </div>
          </div>
        )}
      />
    </div>
  )
}
